import { useEffect, useRef, RefObject } from 'react'

/* ================================================================================
   useModalA11y — keyboard accessibility for portal dialogs/drawers (WCAG 2.2):
   - On open: move focus into the panel (first focusable, else the panel itself).
   - While open: trap Tab/Shift+Tab within the panel so focus cannot escape to the
     page behind (aria-modal expectation).
   - Escape closes the dialog.
   - On close: restore focus to the element that had focus before the dialog opened
     (the trigger), so keyboard users resume where they left off.
   Usage: const panelRef = useRef<HTMLDivElement>(null)
          useModalA11y(open, onClose, panelRef)
          <div ref={panelRef} role="dialog" aria-modal="true" ...>
   The hook depends only on `open`, so it is safe even if `onClose` is an inline fn.
   ================================================================================ */

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusables(el: HTMLElement | null): HTMLElement[] {
  if (!el) return []
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (n) => n.tabIndex >= 0 && n.getClientRects().length > 0,
  )
}

export function useModalA11y(
  open: boolean,
  onClose: () => void,
  panelRef: RefObject<HTMLElement>,
) {
  // Keep latest onClose without re-triggering the effect on every render.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    // Move focus into the panel once it has rendered.
    const panel = panelRef.current
    if (panel) {
      const f = focusables(panel)
      ;(f[0] ?? panel).focus({ preventScroll: true })
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const p = panelRef.current
      if (!p) return
      const f = focusables(p)
      if (f.length === 0) {
        e.preventDefault()
        p.focus()
        return
      }
      const first = f[0]
      const last = f[f.length - 1]
      const active = document.activeElement as HTMLElement
      if (e.shiftKey && (active === first || !p.contains(active))) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      // Restore focus to the trigger that opened the dialog.
      previouslyFocused?.focus?.()
    }
  }, [open, panelRef])
}